import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Card,
  Button,
  Tag,
  Space,
  Modal,
  Input,
  Switch,
  Dropdown,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  CopyOutlined,
  DeleteOutlined,
  EyeOutlined,
  MoreOutlined,
  StarOutlined,
  StarFilled,
} from "@ant-design/icons";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getAllFlows,
  deleteFlow,
  updateFlow,
  setDefaultFlow,
  createFlow,
  getFlowById,
} from "../../api/productionApi";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const ProductionFlowList = () => {
  // ── Auth ───────────────────────────────────────────────────
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");

  // ── State ──────────────────────────────────────────────────
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cloneModalVisible, setCloneModalVisible] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [newFlowName, setNewFlowName] = useState("");
  const [cloning, setCloning] = useState(false);
  const navigate = useNavigate();

  // ── Fetch flows ────────────────────────────────────────────
  const fetchFlows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllFlows();
      setFlows(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error("Failed to load production flows");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFlows();
  }, [fetchFlows]);

  // ── Auth guard (after hooks) ───────────────────────────────
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }
  checkTokenExp(accessToken);

  // ── Clone flow ─────────────────────────────────────────────
  // Backend has no dedicated clone endpoint, so we fetch the full
  // flow and re-create it with a new name.
  const handleClone = async () => {
    if (!newFlowName.trim()) {
      toast.warning("Please enter a name for the cloned flow");
      return;
    }
    setCloning(true);
    try {
      const source = await getFlowById(selectedFlow.id);
      const payload = {
        name: newFlowName.trim(),
        description: source.description || "",
        applicable_box_types: source.applicable_box_types || [],
        is_default: false,
        stages: (source.stages || []).map((s) => ({
          code: s.code,
          name: s.name,
          stage_type: s.stage_type,
          machine_type: s.machine_type || undefined,
          estimated_hours: s.estimated_hours || undefined,
          buffer_applies: s.buffer_applies,
          auto_advance: s.auto_advance,
          requires_qc: s.requires_qc,
          color: s.color,
        })),
      };
      await createFlow(payload);
      toast.success("Flow cloned successfully");
      setCloneModalVisible(false);
      setNewFlowName("");
      setSelectedFlow(null);
      fetchFlows();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to clone flow");
    } finally {
      setCloning(false);
    }
  };

  // ── Delete flow ────────────────────────────────────────────
  const handleDelete = (record) => {
    Modal.confirm({
      title: "Delete Production Flow",
      content: (
        <>
          This will permanently delete <strong>{record.name}</strong>. Existing
          jobs using this flow won't be affected. Continue?
        </>
      ),
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          await deleteFlow(record.id);
          toast.success("Flow deleted");
          fetchFlows();
        } catch (error) {
          toast.error(error.response?.data?.message || "Failed to delete flow");
        }
      },
    });
  };

  // ── Toggle active ──────────────────────────────────────────
  const toggleActive = async (record) => {
    try {
      // The backend update endpoint accepts any updatable fields.
      // We send the full payload with the toggled is_active.
      await updateFlow(record.id, {
        name: record.name,
        description: record.description,
        applicable_box_types: record.applicable_box_types,
        is_default: record.is_default,
        is_active: record.is_active === false ? true : false,
      });
      toast.success(
        `Flow ${record.is_active === false ? "activated" : "deactivated"}`
      );
      fetchFlows();
    } catch (error) {
      toast.error("Failed to update flow");
    }
  };

  // ── Set default ────────────────────────────────────────────
  const handleSetDefault = async (record) => {
    try {
      await setDefaultFlow(record.id);
      toast.success(`"${record.name}" is now the default flow`);
      fetchFlows();
    } catch (error) {
      toast.error("Failed to set default flow");
    }
  };

  // ── Table columns ──────────────────────────────────────────
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <a
          onClick={() =>
            navigate(`/settings/production-flow/${record.id}`)
          }
          style={{ fontWeight: 500 }}
        >
          {name}
        </a>
      ),
    },
    {
      title: "Product Types",
      dataIndex: "applicable_box_types",
      key: "applicable_box_types",
      render: (types) => (
        <Space wrap size={[4, 4]}>
          {(types || []).map((t) => (
            <Tag key={t}>
              {t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Stages",
      key: "stage_count",
      align: "center",
      width: 80,
      render: (_, record) => (record.stages || []).length,
    },
    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      width: 90,
      render: (active, record) => (
        <Switch
          checked={active !== false}
          onChange={() => toggleActive(record)}
          checkedChildren="Yes"
          unCheckedChildren="No"
          size="small"
        />
      ),
    },
    {
      title: "Default",
      dataIndex: "is_default",
      key: "is_default",
      width: 90,
      render: (val, record) =>
        val ? (
          <Tag icon={<StarFilled />} color="blue">
            Default
          </Tag>
        ) : (
          <Tooltip title="Set as default">
            <Button
              type="text"
              size="small"
              icon={<StarOutlined style={{ color: "#999" }} />}
              onClick={() => handleSetDefault(record)}
            />
          </Tooltip>
        ),
    },
    {
      title: "Stages Preview",
      key: "stages_preview",
      ellipsis: true,
      render: (_, record) => (
        <Space wrap size={[2, 2]}>
          {(record.stages || [])
            .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
            .map((s, i) => (
              <Tag
                key={s.id || i}
                color={s.color || undefined}
                style={{ fontSize: 11 }}
              >
                {s.name}
              </Tag>
            ))}
        </Space>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 50,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "view",
                icon: <EyeOutlined />,
                label: "View / Edit",
                onClick: () =>
                  navigate(`/settings/production-flow/${record.id}`),
              },
              {
                key: "clone",
                icon: <CopyOutlined />,
                label: "Clone",
                onClick: () => {
                  setSelectedFlow(record);
                  setNewFlowName(`${record.name} (Copy)`);
                  setCloneModalVisible(true);
                },
              },
              { type: "divider" },
              {
                key: "delete",
                icon: <DeleteOutlined />,
                label: "Delete",
                danger: true,
                disabled: record.is_default,
                onClick: () => handleDelete(record),
              },
            ],
          }}
          trigger={["click"]}
        >
          <Button icon={<MoreOutlined />} size="small" type="text" />
        </Dropdown>
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────
  return (
    <>
      <PageTitle title="Back" subtitle="Production Flow Configuration" />

      <Card
        title="Production Flows"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/settings/production-flow/new")}
          >
            Create Flow
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={flows}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
          locale={{ emptyText: "No production flows configured yet" }}
        />
      </Card>

      {/* Clone Modal */}
      <Modal
        title="Clone Production Flow"
        open={cloneModalVisible}
        onOk={handleClone}
        confirmLoading={cloning}
        onCancel={() => {
          setCloneModalVisible(false);
          setNewFlowName("");
          setSelectedFlow(null);
        }}
        okText="Clone"
      >
        <p>
          Cloning: <strong>{selectedFlow?.name}</strong>
        </p>
        <Input
          placeholder="New flow name"
          value={newFlowName}
          onChange={(e) => setNewFlowName(e.target.value)}
          onPressEnter={handleClone}
        />
      </Modal>
    </>
  );
};

export default ProductionFlowList;
